import { styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Button, Container, Divider, Icon, Paper, RubricTable, Stack, Tag, Text } from 'shell/components';
import { FEEDBACK_LIMIT } from 'utils/constants';

const Mobile = ({ assignment, batch, feedback, onClickBack, onClickGetFeedback, onClickViewFeedback }) => {
    const { t } = useTranslation('assignment');

    return (
        <Container device={'mobile'}>
            <CustomStack alignItems={'flex-start'} spacing={5}>
                <Button startIcon={<Icon kind={'arrowLeft'} />} variant={'borderless'} onClick={onClickBack}>
                    {t('common:controls.back', 'Back')}
                </Button>
                <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                    {assignment.title}
                </Text>
                <CustomPaper elevation={0}>
                    <Stack divider={<Divider />} spacing={4}>
                        <Stack spacing={4}>
                            <Stack spacing={2}>
                                <Tag type={'attempts'} value={FEEDBACK_LIMIT - (feedback.length + batch.length)} />
                                <Text component={'p'} variant={'h6'} weight={'medium'}>
                                    {t('feedback-section.assignment-feedback-label', 'Assignment Feedback')}
                                </Text>
                                <Stack spacing={5}>
                                    <Text>
                                        {t(
                                            'feedback-section.instant-student-feedback.how-it-works-description',
                                            "TimelyGrader AI utilizes the assignment rubric and examples of your instructor's feedback to deliver personalized feedback tailored to this deliverable. This gives you a chance to get feedback and improve your work before you submit!"
                                        )}
                                    </Text>
                                    <Text>
                                        {t(
                                            'feedback-section.instant-student-feedback.important-note',
                                            "Important: Submitting drafts is not considered your final submission. Please submit your final work as per your instructor's instructions."
                                        )}
                                    </Text>
                                </Stack>
                            </Stack>
                            <Button
                                disabled={feedback.length + batch.length >= FEEDBACK_LIMIT}
                                onClick={onClickGetFeedback}
                            >
                                {t(
                                    'feedback-section.instant-student-feedback.upload-a-draft-count',
                                    'Upload a draft ({{draftCount}}/{{maxDraftCount}})',

                                    {
                                        draftCount: FEEDBACK_LIMIT - (feedback.length + batch.length),
                                        maxDraftCount: FEEDBACK_LIMIT,
                                    }
                                )}
                            </Button>
                        </Stack>
                        <Stack spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p1'}>
                                    {t('feedback-section.instant-student-feedback.your-feedback', 'Your Feedback')}
                                </Text>
                                <Tag type={'general'} value={'status'} />
                            </Stack>
                            {!feedback.length && !batch.length ? (
                                <Text color={'light'}>
                                    {t(
                                        'feedback-section.instant-student-feedback.nothing-to-show-yet',
                                        'Nothing to show yet! Get started by uploading an assignment to get feedback.'
                                    )}
                                </Text>
                            ) : (
                                <Stack spacing={3}>
                                    {feedback.map((feedbackItem, index) => (
                                        <Stack
                                            key={feedbackItem.id}
                                            sx={{ minWidth: 0 }}
                                            alignItems={'center'}
                                            direction={'row'}
                                            justifyContent={'space-between'}
                                            spacing={2}
                                        >
                                            <Stack>
                                                <Text color={'light'} variant={'p3'}>
                                                    {t(
                                                        'common:ui.format-date-time-sign',
                                                        '{{val, date_short}} @ {{val, time_only}}',
                                                        {
                                                            val: feedbackItem.created_on,
                                                        }
                                                    )}{' '}
                                                </Text>
                                                <Text>
                                                    {typeof feedbackItem.feedback === 'string'
                                                        ? t('attempt-status', 'Attempt {{attemptStatus}}', {
                                                              attemptStatus: index + 1,
                                                          })
                                                        : feedbackItem.submission.name}
                                                </Text>
                                            </Stack>
                                            <Button
                                                variant={'borderless'}
                                                onClick={() => onClickViewFeedback(feedbackItem)}
                                            >
                                                {t('common:controls.view', 'View')}
                                            </Button>
                                        </Stack>
                                    ))}
                                    {batch.map((batchItem) => (
                                        <Stack
                                            key={batchItem.id}
                                            sx={{ minWidth: 0 }}
                                            alignItems={'center'}
                                            direction={'row'}
                                            justifyContent={'space-between'}
                                            spacing={2}
                                        >
                                            <Stack>
                                                <Text color={'light'} variant={'p3'}>
                                                    {t(
                                                        'common:ui.format-date-time-sign',
                                                        '{{val, date_short}} @ {{val, time_only}}',
                                                        {
                                                            val: batchItem.created_on,
                                                        }
                                                    )}
                                                </Text>
                                                <Text>{batchItem.submissions?.[0].files?.[0].data.name}</Text>
                                            </Stack>
                                            <Text sx={{ flexShrink: 0 }} color={'light'}>
                                                {t('common:ui.processing', 'Processing')}...
                                            </Text>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                </CustomPaper>
                <CustomPaper elevation={0}>
                    <Stack spacing={5}>
                        <Stack spacing={2}>
                            <Text>{t('assignment-description-label', 'Assignment Description')}</Text>
                            {assignment.description ? (
                                <Text color={'light'} rich>
                                    {assignment.description}
                                </Text>
                            ) : (
                                <Text color={'light'}>{t('common:ui.none', 'None')}</Text>
                            )}
                        </Stack>
                        <Stack spacing={2}>
                            <Text>{t('assignment-rubric-label', 'Assignment Rubric')}</Text>
                            {assignment.rubric ? (
                                <RubricTable isExpanded type={'view'} data={assignment.rubric.criteria} />
                            ) : (
                                <Text color={'light'}>{t('common:ui.none', 'None')}</Text>
                            )}
                        </Stack>
                    </Stack>
                </CustomPaper>
            </CustomStack>
        </Container>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        paddingBottom: theme.spacing(5),
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        borderRadius: '12px',
        width: '100%',
        padding: theme.spacing(5),
    };
});

export default Mobile;
